<script setup lang="ts">
import { computed } from 'vue';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { formatCurrency } from '@/utils/currency';
import { type Payment } from '@/api/payments';
import PaymentMovementForm from '@/components/payments/payment-movement-form.vue';
import PaymentMovementDestroyForm from '@/components/payments/payment-movement-destroy-form.vue';
import SalaryPaymentDetail from '@/components/payments/salary-payment-detail.vue';
import ShiftsPaymentDetail from '@/components/payments/shifts-payment-detail.vue';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

interface Props {
  payment: Payment;
}

const props = defineProps<Props>();

const emit = defineEmits(['close']);

const { tableProps } = useResponsiveTable();

const hasSalaryPayment = computed(() => !!props.payment.salaryPayment);
const hasPerShiftPayment = computed(() => !!props.payment.perShiftPayment);

const perShiftTitle = computed(() => (
  hasSalaryPayment.value ? 'Turnos por Pago Directo' : 'Turnos'
));

const paymentMovementHeaders = [
  { title: 'Fecha', value: 'date' },
  { title: 'Monto', value: 'amount' },
  { title: 'Descripción', value: 'description' },
  { title: 'Boleta', value: 'withTaxBill' },
  { title: 'Acciones', value: 'actions' },
];

function localizedDate(date: string) {
  return date.split('-').reverse().join('/');
}
</script>
<template>
  <v-card class="pr-8">
    <v-card-title class="align-baseline d-flex">
      {{ `Detalles del Pago a ${props.payment.caregiver.fullName}` }}
      <v-spacer />
      <v-btn
        icon="mdi-window-close"
        variant="plain"
        @click="emit('close')"
      />
    </v-card-title>
    <v-expansion-panels class="ma-4">
      <!-- Salary Payment Section -->
      <salary-payment-detail
        v-if="hasSalaryPayment"
        :salary-payment="props.payment.salaryPayment!"
      />

      <!-- Per-Shift Payment Section -->
      <shifts-payment-detail
        v-if="hasPerShiftPayment"
        :title="perShiftTitle"
        :payments-summary="props.payment.perShiftPayment!.paymentsSummary"
        :shifts-count="props.payment.perShiftPayment!.shiftsCount"
        :shifts-count-with-taxes="props.payment.perShiftPayment!.shiftsCountWithTaxes"
        :cost="props.payment.perShiftPayment!.cost"
        :taxes="props.payment.perShiftPayment!.taxes"
        :total-cost="props.payment.perShiftPayment!.totalCost"
      />

      <v-expansion-panel class="mt-4">
        <v-expansion-panel-title class="font-weight-bold">
          Movimientos
          <v-spacer />
          <v-chip
            :color="props.payment.totalPaymentMovements < 0 ? 'error' : 'green-darken-3'"
            class="mr-8"
          >
            {{ formatCurrency(props.payment.totalPaymentMovements) }}
          </v-chip>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-data-table
            v-bind="tableProps"
            :headers="paymentMovementHeaders"
            :items="props.payment.paymentMovements"
            :header-props="{ class: 'font-weight-bold' }"
            class="elevation-1"
            no-data-text="No hay movimientos"
          >
            <template #top>
              <v-toolbar
                color="white"
                flat
              >
                <v-spacer />
                <responsive-dialog
                  max-width="800"
                  scrollable
                >
                  <template #activator="{ props: activatorProps }">
                    <v-btn
                      v-bind="activatorProps"
                      icon="mdi-plus"
                      color="primary"
                      size="small"
                      elevation="2"
                    />
                  </template>
                  <template #default="{ close }">
                    <payment-movement-form
                      :caregiver-id="props.payment.caregiver.id"
                      mode="new"
                      @close="close"
                    />
                  </template>
                </responsive-dialog>
              </v-toolbar>
            </template>
            <template #item.date="{ item }">
              {{ localizedDate(item.date) }}
            </template>
            <template #item.amount="{ item }">
              <v-chip :color="item.movementType === 'payment' ? 'error' : 'green-darken-3'">
                {{ formatCurrency(item.amount) }}
              </v-chip>
            </template>
            <template #item.withTaxBill="{ item }">
              {{ item.withTaxBill ? 'Sí' : 'No' }}
            </template>
            <template #item.actions="{ item }">
              <responsive-dialog
                max-width="800"
                scrollable
              >
                <template #activator="{ props: activatorProps }">
                  <v-btn
                    v-bind="activatorProps"
                    icon="mdi-pencil"
                    size="small"
                    class="me-2"
                  />
                </template>
                <template #default="{ close }">
                  <payment-movement-form
                    :payment-movement="item"
                    :caregiver-id="props.payment.caregiver.id"
                    mode="edit"
                    @close="close"
                  />
                </template>
              </responsive-dialog>
              <responsive-dialog
                max-width="800"
                scrollable
              >
                <template #activator="{ props: activatorProps }">
                  <v-btn
                    v-bind="activatorProps"
                    icon="mdi-delete"
                    size="small"
                    class="me-2"
                  />
                </template>

                <template #default="{ close }">
                  <payment-movement-destroy-form
                    :payment-movement-id="item.id"
                    @close="close"
                  />
                </template>
              </responsive-dialog>
            </template>
            <template #body.append>
              <tr>
                <th class="font-weight-bold">
                  Total
                </th>
                <th>
                  <v-chip :color="props.payment.totalPaymentMovements < 0 ? 'error' : 'green-darken-3'">
                    {{ formatCurrency(props.payment.totalPaymentMovements) }}
                  </v-chip>
                </th>
                <th>-</th>
                <th>-</th>
                <th>-</th>
              </tr>
            </template>
          </v-data-table>
          <p
            v-if="props.payment.movementsTaxes > 0"
            class="mt-3 text-body-2"
          >
            Retención de los ajustes con boleta:
            <strong>{{ formatCurrency(props.payment.movementsTaxes) }}</strong>
            (se suma al Monto Bruto)
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>
      <v-expansion-panel
        class="mt-4"
        readonly
      >
        <v-expansion-panel-title
          class="font-weight-bold"
          expand-icon=""
        >
          Total a Pagar
          <v-spacer />
          <v-chip color="primary mr-8">
            {{ formatCurrency(props.payment.amountPendingToPay) }}
          </v-chip>
        </v-expansion-panel-title>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>
